import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { modernConfirm } from '../../admin-shared/modernDialog';
import {
  cancelServiceLines,
  completeServiceLines,
  fetchActiveVisit,
} from '../../salon-admin/utils/checkinQueueApi';
import {
  SALON_SERVICE_LOBBY_PATH,
  SALON_SERVICE_SESSION_PATH,
} from '../config/salonApp';
import {
  coerceServiceStatus,
  ongoingLines,
  serviceLineLabel,
  SERVICE_CANCELLED,
  SERVICE_COMPLETED,
} from '../utils/serviceLineUtils';
import { pinServiceWork, removeServiceWorkPin } from '../utils/serviceWorkStorage';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './ServiceSessionPage.css';

export default function ServiceWorkPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell();
  const homePath = resolveSalonHomePath();

  const checkinId = location.state?.checkinId;
  const stylistId = String(location.state?.stylistId || '').trim();
  const stylistName = String(location.state?.stylistName || '').trim();
  const customerCode = location.state?.checkinCode || '';
  const customerName = location.state?.customerName || '';

  const [checkin, setCheckin] = useState(null);
  const [selectedLineIds, setSelectedLineIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!checkinId) return null;
    setLoading(true);
    setError('');
    try {
      const row = await fetchActiveVisit({ checkinId });
      if (!row?.id) throw new Error('Visit not found or already closed');
      setCheckin(row);
      pinServiceWork({
        checkinId: row.id,
        customerCode: row.customer_code || customerCode,
        customerName: row.customer_name || customerName,
        stylistId,
        stylistName,
      });
      return row;
    } catch (err) {
      setError(err?.message || 'Could not load visit');
      return null;
    } finally {
      setLoading(false);
    }
  }, [checkinId, customerCode, customerName, stylistId, stylistName]);

  useEffect(() => {
    if (!checkinId) {
      navigate(SALON_SERVICE_LOBBY_PATH, { replace: true });
      return;
    }
    load();
  }, [checkinId, load, navigate]);

  const inProgress = useMemo(
    () => ongoingLines(checkin?.selected_services, stylistId),
    [checkin?.selected_services, stylistId],
  );

  const completed = useMemo(() => {
    const list = Array.isArray(checkin?.selected_services) ? checkin.selected_services : [];
    return list.filter((line) => {
      const st = coerceServiceStatus(line?.service_status);
      if (st !== SERVICE_COMPLETED) return false;
      if (!stylistId) return true;
      const staff = line?.staff || [];
      const ids = Array.isArray(staff) ? staff.map((s) => String(s.id)) : [];
      return !ids.length || ids.includes(stylistId);
    });
  }, [checkin?.selected_services, stylistId]);

  const cancelled = useMemo(() => {
    const list = Array.isArray(checkin?.selected_services) ? checkin.selected_services : [];
    return list.filter((line) => coerceServiceStatus(line?.service_status) === SERVICE_CANCELLED);
  }, [checkin?.selected_services]);

  const toggleLine = (lineId) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const goLobbyIfVisitClosed = (updated) => {
    const status = String(updated?.status || '').toLowerCase();
    if (status === 'completed' || status === 'cancelled') {
      removeServiceWorkPin(checkinId);
      setTimeout(() => navigate(SALON_SERVICE_LOBBY_PATH, { replace: true }), 800);
      return true;
    }
    return false;
  };

  const completeSelected = async () => {
    const ids = [...selectedLineIds];
    if (!ids.length) {
      setError('Select at least one in-progress service');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const updated = await completeServiceLines(checkinId, ids);
      setCheckin(updated);
      setSelectedLineIds(new Set());
      setNotice('Service marked complete — front desk updated.');
      try {
        window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
      } catch {
        /* ignore */
      }
      goLobbyIfVisitClosed(updated);
    } catch (err) {
      setError(err?.message || 'Failed to complete');
    } finally {
      setBusy(false);
    }
  };

  const cancelSelected = async () => {
    const ids = [...selectedLineIds];
    if (!ids.length) {
      setError('Select at least one started service to cancel');
      return;
    }
    const labels = inProgress
      .filter((line) => ids.includes(line.line_id || line.id))
      .map((line) => serviceLineLabel(line, { includeStaff: false }));
    const detail = labels.length ? `\n\n${labels.join('\n')}` : '';
    const ok = await modernConfirm(
      `Cancel the started service(s)? This cannot be undone.${detail}`,
      {
        title: 'Cancel started service',
        confirmLabel: 'Cancel service',
        cancelLabel: 'Keep working',
        danger: true,
      },
    );
    if (!ok) return;

    setBusy(true);
    setError('');
    try {
      const updated = await cancelServiceLines(checkinId, ids);
      setCheckin(updated);
      setSelectedLineIds(new Set());
      setNotice('Started service cancelled — stylist unlocked if no other services remain.');
      try {
        window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
      } catch {
        /* ignore */
      }
      if (!goLobbyIfVisitClosed(updated)) {
        const stillGoing = ongoingLines(updated?.selected_services, stylistId);
        if (!stillGoing.length) {
          removeServiceWorkPin(checkinId, stylistId);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  const addNewService = () => {
    navigate(SALON_SERVICE_SESSION_PATH, {
      state: {
        checkinId,
        checkinCode: customerCode || checkin?.customer_code,
        customerName: customerName || checkin?.customer_name,
        stylistId,
        stylistName,
        visitIntent: 'service',
        addMore: true,
      },
    });
  };

  const guestTitle = customerName || checkin?.customer_name || 'Guest';

  return (
    <div className="fi-lux-theme fi-scroll-page salon-service-session">
      <div className="salon-service-session__content">
        <header className="salon-service-session__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
          <FaceIdentityFlowNav
            homePath={homePath}
            onBack={() => navigate(SALON_SERVICE_LOBBY_PATH, { replace: false })}
          />
        </header>
        <h1 className="salon-service-session__title">Complete service</h1>
        <p className="salon-service-session__lede">{guestTitle}</p>
        {stylistName ? (
          <p className="salon-service-session__banner" role="status">
            Stylist · {stylistName}
          </p>
        ) : null}

        {error ? <p className="salon-service-session__error">{error}</p> : null}
        {notice ? <p className="salon-service-session__notice">{notice}</p> : null}

        {loading ? (
          <p className="salon-service-session__lede">Loading…</p>
        ) : (
          <div className="salon-service-session__panel">
            <h2 className="salon-service-session__subtitle">Tap services you finished or want to cancel</h2>
            {!inProgress.length ? (
              <p className="salon-service-session__lede">No services in progress for this stylist.</p>
            ) : (
              <div className="salon-service-session__staff-grid" role="group">
                {inProgress.map((line) => {
                  const lid = line.line_id || line.id;
                  const active = selectedLineIds.has(lid);
                  return (
                    <button
                      key={lid}
                      type="button"
                      className={
                        active
                          ? 'salon-service-session__staff is-active'
                          : 'salon-service-session__staff'
                      }
                      onClick={() => toggleLine(lid)}
                    >
                      <strong>{serviceLineLabel(line, { includeStaff: false })}</strong>
                      <span>In progress</span>
                    </button>
                  );
                })}
              </div>
            )}

            {completed.length ? (
              <>
                <h2 className="salon-service-session__subtitle">Completed this visit</h2>
                <ul className="salon-service-lobby__list">
                  {completed.map((line) => (
                    <li key={line.line_id || line.id} className="salon-service-lobby__row salon-service-lobby__row--static">
                      <strong>{serviceLineLabel(line, { includeStaff: false })}</strong>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {cancelled.length ? (
              <>
                <h2 className="salon-service-session__subtitle">Cancelled</h2>
                <ul className="salon-service-lobby__list">
                  {cancelled.map((line) => (
                    <li key={line.line_id || line.id} className="salon-service-lobby__row salon-service-lobby__row--static">
                      <strong>{serviceLineLabel(line, { includeStaff: false })}</strong>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <div className="salon-service-session__actions">
              <button
                type="button"
                className="salon-service-session__secondary"
                disabled={busy}
                onClick={addNewService}
              >
                Add new service
              </button>
              <button
                type="button"
                className="salon-service-session__secondary"
                disabled={busy || !selectedLineIds.size}
                onClick={cancelSelected}
              >
                Cancel started
              </button>
              <button
                type="button"
                className="salon-service-session__primary"
                disabled={busy || !selectedLineIds.size}
                onClick={completeSelected}
              >
                {busy ? 'Saving…' : 'Mark complete'}
              </button>
            </div>
            <button
              type="button"
              className="salon-service-session__link"
              onClick={() => navigate(SALON_SERVICE_LOBBY_PATH)}
            >
              Back to stylist desk
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
